/**
 * AliasMatchNote — say WHY a `/farm` search hit matched (#2842).
 *
 * A farmer knows the Schlag as ``E100-E109 Karima`` in wiberry and as
 * ``Lange Striche (100-109)`` in MyFarm24. Typing the former already returns the
 * row — the server's ``q`` substring-matches the whole ``attributes`` JSONB cast
 * to text — but the row comes back titled with the *other* name, which reads as
 * the wrong result. This note is the missing half sentence:
 *
 *     gefunden über wiberry-Bezeichnung „E100-E109 Karima“
 *
 * Nothing here widens what search FINDS; it is attribution only.
 *
 * ⚠️ Criterion 7 — the alias is quoted as *what a system calls this field*,
 * never offered as the field's name. The row's title stays the canonical one,
 * and only that name is hashed into ``field_id``.
 */
import React from 'react';
import { matchedAliases } from './object/nameAliases';
import {
    DEFAULT_SOURCE_SYSTEMS,
    resolveProvenanceSource,
    type SourceSystemEntry,
} from './object/provenanceSource';
import './AliasMatchNote.css';

/**
 * The "gefunden über …" note, or ``undefined`` when the row needs no
 * explanation — no active search, a hit on the canonical name, or no matching
 * alias. ``undefined`` (not an empty element) so the feed row renders no tag
 * slot at all.
 */
export function renderAliasMatch(
    raw: unknown,
    search: string | null,
    canonicalName: unknown,
    // #3385 — the source-system registry, for symmetry with `PropertyGrid`:
    // this is a plain function, not a component, so it cannot fetch and the
    // table has to be handed in. `FarmPage` threads the fetched one, so the
    // feed and the object view attribute the SAME alias identically; the
    // default keeps every other caller (and this file's tests) on the
    // bundled five.
    systems: ReadonlyArray<SourceSystemEntry> = DEFAULT_SOURCE_SYSTEMS,
): React.ReactNode | undefined {
    if (!search) return undefined;
    const matches = matchedAliases(raw, search, canonicalName);
    if (matches.length === 0) return undefined;
    // One line even when several spellings match: the point is to explain the
    // hit, not to enumerate the field's whole vocabulary — the field card does
    // that. The first match is the array's own stable order, not a ranking.
    const [alias] = matches;
    const provenance = alias.sourceSystem
        ? resolveProvenanceSource(alias.sourceSystem, undefined, systems)
        : null;
    const system = provenance?.kind === 'system' ? provenance.label : null;
    // Durchkopplung (#3385): the label is COMPOUNDED into the next word, and
    // German hyphenates every part of a multi-word proper noun in a compound
    // ("New-York-Reise"). The bundled five are all single words, so this was
    // moot until the server registry brought "John Deere" and "Databricks IDM"
    // within reach — "John Deere-Bezeichnung" is simply wrong, and the reader
    // is a German farmer.
    const compounded = system?.replace(/\s+/g, '-');
    const label = compounded
        ? `gefunden über ${compounded}-Bezeichnung „${alias.value}“`
        : `gefunden über frühere Bezeichnung „${alias.value}“`;
    return (
        <span className="alias-match-note" role="note" aria-label={label}>
            {label}
        </span>
    );
}

export default renderAliasMatch;
